/**
 * Custom Axios Instance for DocBits SDKs
 *
 * This is the bridge between generated Orval SDKs and the existing DocBits infrastructure.
 *
 * Responsibilities:
 * 1. Inject Bearer token from auth store (automatic)
 * 2. Inject X-ORG-ID header for multi-tenancy (automatic)
 * 3. Handle token refresh on 401 errors (automatic)
 * 4. Preserve request/response interceptor logic
 * 5. Support request cancellation
 *
 * Usage in Generated SDKs:
 * All generated SDK functions will use this mutator automatically via Orval config.
 * No manual configuration needed.
 *
 * Example Generated Code:
 *   export const getDocumentById = (
 *     documentId: string,
 *     options?: AxiosRequestConfig
 *   ) => {
 *     return customAxiosInstance<Document>({
 *       url: `/document/${documentId}`,
 *       method: 'GET',
 *       ...options,
 *     });
 *   };
 */

import axios, {
  AxiosRequestConfig,
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import type { useAuthTokenStore } from 'src/store/User/auth_token.store';
import { getOrgId } from 'src/utils/organization/org_id_helper';
import { DOC2API_URL } from 'src/pages/APIs';
import { API_TIMEOUTS } from '@/config/api-timeouts';

// Extended request config with custom properties for retry logic and timing
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  __requestStartTime?: number;
  __isRetry?: boolean;
}

// ============================================================================
// Axios Instance Setup
// ============================================================================

/**
 * Create base axios instance without interceptors initially.
 * Interceptors will be added after store is available.
 *
 * CRITICAL FIX (Nov 22, 2025):
 * Added baseURL: DOC2API_URL to ensure all SDK requests go to the correct API domain.
 * Without this, Orval-generated SDKs would make relative URL requests that resolve
 * to the wrong domain (e.g., dev.docbits.com instead of dev.api.docbits.com).
 * This baseURL applies to all 94 SDK files across 8 services.
 *
 * TIMEOUT CONFIGURATION (Jan 7, 2026):
 * Default timeout is 60s for most endpoints. Individual SDK wrappers can override
 * this with longer timeouts for slow endpoints (e.g., dashboard with complex filters).
 * See @/config/api-timeouts.ts for adaptive timeout configuration.
 */
export const AXIOS_INSTANCE = axios.create({
  baseURL: DOC2API_URL,
  timeout: API_TIMEOUTS.DEFAULT, // 60 second default, can be overridden per request
  headers: {
    'Content-Type': 'application/json',
  },
});

// baseURL is set from DOC2API_URL at construction time. The region prefix is
// baked into that constant by api-base-url.ts, and a region switch reloads
// the page — so we never need to mutate baseURL at runtime.

// Transient-retry sits BEFORE the response interceptors below so a
// retry that succeeds on attempt 2 never reaches the error-path
// event dispatcher → the banner stays hidden for sub-second
// rolling-deploy blips. Matches the ApiClient.ts install location.
import { installTransientRetry } from 'src/core/transientRetry';
import { attachTraceHeaders } from 'src/utils/searchTrace';
installTransientRetry(AXIOS_INSTANCE);

/**
 * Track pending requests to prevent duplicate token refresh calls.
 * When a 401 occurs, if refresh is already in progress, queue the request.
 */
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: string | null) => void;
  reject: (reason: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null = null): void {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  isRefreshing = false;
  failedQueue = [];
}

// ============================================================================
// Request Interceptor
// ============================================================================

/**
 * Request Interceptor: Add token and org_id headers
 *
 * This runs before every SDK request to:
 * 1. Add Bearer token from auth store (if available)
 * 2. Add X-ORG-ID header for multi-tenancy
 * 3. Track request start time for performance logging
 *
 * CRITICAL FIX: Made async to properly await token from auth store
 */
AXIOS_INSTANCE.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      // Add bearer token if available
      // Note: Auth store import is lazy-loaded to avoid circular dependencies
      // ✅ Await the async getAuthToken() call
      const token = await getAuthToken();
      if (token) {
        config.headers.set('Authorization', `Bearer ${token}`);
      }

      // Add org_id header for multi-tenancy
      const orgId = getOrgId();
      if (orgId) {
        config.headers.set('X-ORG-ID', orgId);
      }

      attachTraceHeaders(config);

      // Track request start time for performance monitoring
      (config as ExtendedAxiosRequestConfig).__requestStartTime = Date.now();

      return config;
    } catch (error) {
      // If interceptor fails, continue without token/org_id
      console.warn('Warning: Failed to add auth headers to request:', error);
      return config;
    }
  },
  (error: unknown) => {
    return Promise.reject(error);
  },
);

// ============================================================================
// Response Interceptor
// ============================================================================

/**
 * Response Interceptor: Log performance and handle success
 */
AXIOS_INSTANCE.interceptors.response.use(
  (response: AxiosResponse) => {
    // Log request duration for performance monitoring
    const startTime = (response.config as ExtendedAxiosRequestConfig)
      .__requestStartTime;
    if (startTime) {
      const duration = Date.now() - startTime;
      if (duration > 5000) {
        // Log slow requests (> 5 seconds)
        console.warn(
          `Slow API request: ${response.config.url} took ${duration}ms`,
        );
      }
    }

    // Signal recovery: any 2xx unlocks the backend-unavailable
    // banner. Mirrors ApiClient.handleResponse so the banner reacts
    // symmetrically whether requests come from the hand-written
    // client or the generated SDK.
    if (typeof window !== 'undefined' && typeof CustomEvent !== 'undefined') {
      window.dispatchEvent(new CustomEvent('app:backend-available'));
    }

    return response;
  },
  (error: AxiosError) => {
    return handleResponseError(error);
  },
);

// Register debug collector interceptor for API call capture (Ctrl+Shift+P debug snapshot)
import('src/boot/debugCollector').then(({ registerDebugInterceptor }) => {
  registerDebugInterceptor(AXIOS_INSTANCE);
}).catch(() => { /* debugCollector not yet available during early boot */ });

// ============================================================================
// Error Handling
// ============================================================================

/**
 * Handle response errors with appropriate retry logic
 *
 * - 401: Token expired, attempt refresh and retry
 * - 403: Invalid/revoked token, logout
 * - 503: Service unavailable, show error page
 * - Other: Pass through to caller
 */
async function handleResponseError(error: AxiosError): Promise<never> {
  const originalRequest = error.config as ExtendedAxiosRequestConfig | undefined;

  // Handle 401 Unauthorized - Token expired or invalid
  if (error.response?.status === 401 && originalRequest) {
    // Prevent infinite loop - if request was already retried, don't try again
    if (originalRequest.__isRetry) {
      return Promise.reject(error);
    }

    if (!isRefreshing) {
      isRefreshing = true;

      try {
        const newToken = await refreshAccessToken();

        if (newToken) {
          processQueue(null, newToken);

          // Retry original request with new token
          originalRequest.headers.set('Authorization', `Bearer ${newToken}`);
          originalRequest.__isRetry = true;
          return AXIOS_INSTANCE(originalRequest);
        } else {
          processQueue(error, null);
          return Promise.reject(error);
        }
      } catch (refreshError) {
        processQueue(refreshError, null);

        // If refresh fails, trigger logout
        handleTokenRefreshFailure();
        return Promise.reject(refreshError);
      }
    } else {
      // Token refresh already in progress, queue this request
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve: (token: string | null) => {
            if (token && originalRequest) {
              originalRequest.headers.set('Authorization', `Bearer ${token}`);
              originalRequest.__isRetry = true;
              resolve(AXIOS_INSTANCE(originalRequest));
            } else {
              reject(error);
            }
          },
          reject: (err: unknown) => {
            reject(err);
          },
        });
      });
    }
  }

  // Handle 403 Forbidden - Token is invalid/revoked, logout user
  if (error.response?.status === 403) {
    console.warn('403 Forbidden: Token is invalid or revoked');
    handleTokenRefreshFailure();
    return Promise.reject(error);
  }

  // 503 Service Unavailable — the backend answered but is unhealthy
  // (overloaded, rolling deploy, broker hiccup). Log it as a handled
  // warning and reject so the caller can handle it inline. This is NOT
  // a connectivity outage, so it does NOT raise the backend-unavailable
  // banner and (as before) does NOT redirect to /network_error.
  if (error.response?.status === 503) {
    console.warn('503 Service Unavailable');
    return Promise.reject(error);
  }

  // Genuine network error — no response was received at all (DNS
  // failure, connection refused, offline). The backend is actually
  // unreachable, so raise the global backend-unavailable banner
  // (picked up by BackendUnavailableBanner in App.vue). Mirrors the
  // Network Error branch in ApiClient.ts so both axios pipelines
  // behave identically.
  if (!error.response && error.code === 'ERR_NETWORK') {
    if (typeof window !== 'undefined' && typeof CustomEvent !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('app:backend-unavailable', {
          detail: {
            url: error.config?.url,
            reason: 'network-error',
            source: 'sdk-axios',
            timestamp: Date.now(),
          },
        }),
      );
    }
    return Promise.reject(error);
  }

  // All other errors (including 503) - pass through to the caller
  return Promise.reject(error);
}

// ============================================================================
// Token Management Helpers
// ============================================================================

/**
 * Get current access token from auth store
 * Uses lazy loading to avoid circular dependencies
 *
 * CRITICAL FIX: Changed from non-existent getAccessToken() to getAuthorizationToken()
 * The auth store uses async getAuthorizationToken() which validates and refreshes tokens
 *
 * IMPORTANT: getAuthorizationToken() returns "Bearer <token>" format.
 * This function strips the "Bearer " prefix and returns raw token value.
 * The Authorization header is added by the request interceptor.
 */
async function getAuthToken(): Promise<string | null> {
  try {
    const { useAuthTokenStore } = await import('src/store/User/auth_token.store');
    const authStore = useAuthTokenStore();

    // ✅ Use the correct async method that actually exists in the store
    const authHeader = await authStore.getAuthorizationToken();

    // Strip "Bearer " prefix if it exists (getAuthorizationToken returns "Bearer <token>")
    // This prevents double "Bearer " prefix when we add it in the request interceptor
    if (!authHeader) {
      return null;
    }

    const token = authHeader.replace(/^Bearer\s+/i, '');
    return token || null;
  } catch (error) {
    console.warn('Warning: Could not retrieve auth token:', error);
    return null;
  }
}

/**
 * Refresh access token using refresh token
 * Uses the existing auth service's token refresh logic
 *
 * IMPORTANT: Similar to getAuthToken(), this strips the "Bearer " prefix
 * from the authorization header to prevent double "Bearer " in requests.
 */
async function refreshAccessToken(): Promise<string | null> {
  try {
    const { useAuthTokenStore } = await import('src/store/User/auth_token.store');
    const authStore = useAuthTokenStore();

    // Trigger token refresh via the store
    const refreshed = await authStore.refreshToken();

    if (!refreshed) {
      return null;
    }

    // After refresh, get the new token using the same method as getAuthToken
    // This ensures we return raw token without "Bearer " prefix
    const authHeader = await authStore.getAuthorizationToken();
    if (!authHeader) {
      return null;
    }

    const token = authHeader.replace(/^Bearer\s+/i, '');
    return token || null;
  } catch (error) {
    console.error('Token refresh failed:', error);
    return null;
  }
}

/**
 * Handle token refresh failure
 * Triggers logout and clears auth state
 */
async function handleTokenRefreshFailure(): Promise<void> {
  try {
    const { useAuthTokenStore } = await import('src/store/User/auth_token.store');
    const authStore = useAuthTokenStore() as any;

    if (typeof authStore.logout === 'function') {
      authStore.logout();
    }
  } catch (error) {
    console.error('Error during logout:', error);
  }
}

// ============================================================================
// SDK Mutator Function
// ============================================================================

/**
 * Custom Axios Mutator Function
 *
 * This is the function that Orval calls for every SDK request.
 * It wraps the axios instance and supports request cancellation.
 *
 * @param config - Axios request configuration
 * @returns Promise that resolves to response data, with optional cancel method
 *
 * Usage Example (Generated by Orval):
 *   const getDocumentById = (documentId: string) => {
 *     return customAxiosInstance<Document>({
 *       url: `/document/${documentId}`,
 *       method: 'GET',
 *     });
 *   };
 */
// Type for cancellable promise
type CancellablePromise<T> = Promise<T> & { cancel?: () => void };

export const customAxiosInstance = <T>(
  config: AxiosRequestConfig,
): CancellablePromise<T> => {
  // Create a cancel token for this request
  const source = axios.CancelToken.source();

  // Execute the request with our configured axios instance
  const promise = AXIOS_INSTANCE({
    ...config,
    cancelToken: source.token,
  }).then(({ data }) => data) as CancellablePromise<T>;

  // Attach cancel method to the promise
  promise.cancel = () => {
    source.cancel('Request was cancelled by the SDK');
  };

  return promise;
};

/**
 * Export as both default and named export for flexibility
 * Some code may use `import customAxiosInstance`
 * Some may use `import { customAxiosInstance }`
 */
export default customAxiosInstance;

// ============================================================================
// Re-export Axios Types
// ============================================================================

export type { AxiosRequestConfig, AxiosError, AxiosResponse };

/**
 * Development Notes:
 *
 * 1. Token Refresh Flow:
 *    - Request fails with 401
 *    - Check if refresh is already in progress
 *    - If not, call refreshAccessToken() from auth store
 *    - If yes, queue request until refresh completes
 *    - Retry original request with new token
 *
 * 2. Multi-Org Support:
 *    - X-ORG-ID header is added to all requests
 *    - Prevents accidentally operating on wrong organization
 *    - Required for multi-tenant SaaS applications
 *
 * 3. Request Cancellation:
 *    - Each SDK request returns a promise with .cancel() method
 *    - Useful for cleanup in Vue components on unmount
 *    - Example: pendingRequest.cancel() in beforeUnmount hook
 *
 * 4. Performance Monitoring:
 *    - Slow requests (> 5s) are logged as warnings
 *    - Useful for debugging and performance optimization
 *    - Logs to console.warn for visibility
 *
 * 5. Circular Dependency Prevention:
 *    - Auth store imports use lazy loading (require, not import)
 *    - Prevents issues during app initialization
 *    - Allows auth system to be set up first
 *
 * 6. Error Handling Priorities:
 *    - 401 errors trigger token refresh + retry
 *    - 403 errors trigger immediate logout
 *    - 503 errors redirect to error page
 *    - Other errors pass through to caller
 *
 * 7. Future Enhancements:
 *    - Add request retry with exponential backoff
 *    - Add request deduplication for GET requests
 *    - Add offline detection and queuing
 *    - Add detailed error logging with request IDs
 */
